import { useMemo, useState } from "react";
import styled from "styled-components";
import { Image } from "antd";
import { ExportOutlined } from "@ant-design/icons";

import { MainColor, SubColor } from "../style/styleValue";
import {
  Publication as PublicationItem,
  PUBLICATIONS,
  PUBLICATION_FOOTNOTE,
  PUBLICATION_HERO,
} from "../component/publication/publications";

type YearFilter = "all" | number;

const Publication = () => {
  const [filter, setFilter] = useState<YearFilter>("all");

  const years = useMemo(
    () => Array.from(new Set(PUBLICATIONS.map((p) => p.year))).sort((a, b) => a - b),
    []
  );
  const visible = filter === "all" ? PUBLICATIONS : PUBLICATIONS.filter((p) => p.year === filter);

  return (
    <Wrapper>
      <Hero>
        <span className="eyebrow">{PUBLICATION_HERO.eyebrow}</span>
        <h1>{PUBLICATION_HERO.title}</h1>
        <p className="subtitle">{PUBLICATION_HERO.subtitle}</p>
        <p className="acknowledgment">{PUBLICATION_HERO.acknowledgment}</p>
      </Hero>

      <SectionHead>
        <h2>
          Publications <small>{String(PUBLICATIONS.length).padStart(2, "0")}</small>
        </h2>
        <Filters>
          {(["all", ...years] as YearFilter[]).map((value) => (
            <FilterButton
              key={value}
              type="button"
              $active={filter === value}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {value === "all" ? "All" : value}
            </FilterButton>
          ))}
        </Filters>
      </SectionHead>

      {visible.map((paper) => (
        <PaperArticle key={paper.id} paper={paper} />
      ))}

      <Footnote>{PUBLICATION_FOOTNOTE}</Footnote>
    </Wrapper>
  );
};

const PaperArticle = ({ paper }: { paper: PublicationItem }) => (
  <Paper>
    <Copy>
      <span className="eyebrow">{paper.date}</span>
      <h3>{paper.title}</h3>
      <p className="authors">{paper.authors}</p>
      <p className="journal">{paper.journal}</p>
      <p className="summary">{paper.summary}</p>
      <footer>
        <a href={paper.url} target="_blank" rel="noopener noreferrer">
          Read publication <ExportOutlined />
        </a>
        <span>{paper.support}</span>
      </footer>
    </Copy>

    {/* 한 논문의 그림끼리는 확대 화면에서 좌우로 넘겨 볼 수 있다 */}
    <Image.PreviewGroup>
      <Figures>
        {paper.figures.map((figure) => (
          <FigureCard key={figure.image}>
            <figcaption>
              <strong>{figure.label}.</strong> {figure.caption}
            </figcaption>
            <Image
              src={figure.image}
              alt={`${figure.label} and original caption`}
              loading="lazy"
              preview={{ mask: "Click to enlarge" }}
            />
            <span className="hint">{figure.label} · Click to enlarge</span>
          </FigureCard>
        ))}
      </Figures>
    </Image.PreviewGroup>
  </Paper>
);

const Wrapper = styled.div`
  width: 100%;
  max-width: 1760px;
  margin: 0 auto;
  padding: 0 38px 40px;
  color: #222;
  background: #fff;

  @media (max-width: 900px) {
    padding: 0 16px 32px;
  }
`;

const Hero = styled.section`
  max-width: 1200px;
  padding: 38px 0 27px;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${SubColor};

    &::before {
      content: "";
      width: 24px;
      height: 2px;
      border-radius: 2px;
      background: currentColor;
    }
  }

  h1 {
    margin: 12px 0 14px;
    font-size: clamp(2.2rem, 4.5vw, 4rem);
    font-weight: 700;
    line-height: 1.12;
    letter-spacing: -0.035em;
    color: ${MainColor};
  }

  .subtitle {
    margin: 0;
    font-size: 1rem;
    line-height: 1.65;
    color: #5b6472;
  }

  .acknowledgment {
    max-width: 1000px;
    margin: 18px 0 0;
    font-size: 0.95rem;
    line-height: 1.7;
    color: #333;
  }
`;

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 2px solid ${MainColor};

  h2 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    color: ${MainColor};

    small {
      margin-left: 4px;
      font-size: 0.8rem;
      font-weight: 500;
      color: #8c8c8c;
    }
  }

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${({ $active }) => ($active ? MainColor : "#d9d9d9")};
  background: ${({ $active }) => ($active ? MainColor : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#555")};
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${MainColor};
    color: ${({ $active }) => ($active ? "#fff" : MainColor)};
  }
`;

// 왼쪽: 논문 정보(스크롤해도 따라온다) / 오른쪽: 그림 목록
const Paper = styled.article`
  display: grid;
  grid-template-columns: minmax(230px, 24%) minmax(0, 1fr);
  gap: 26px;
  align-items: start;
  padding: 36px 0;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    padding: 25px 0;
  }
`;

const Copy = styled.div`
  position: sticky;
  top: 80px;

  .eyebrow {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${SubColor};
  }

  h3 {
    margin: 8px 0 12px;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.45;
    color: ${MainColor};
  }

  p {
    margin: 9px 0;
    font-size: 0.85rem;
    line-height: 1.7;
  }

  .authors {
    color: #5b6472;
  }

  .journal {
    font-weight: 600;
  }

  footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    margin-top: 18px;

    a {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 0.85rem;
      font-weight: 600;
      color: ${SubColor};
      text-decoration: underline;
    }

    span {
      font-size: 0.75rem;
      color: #8c8c8c;
    }
  }

  @media (max-width: 700px) {
    position: static;
  }
`;

const Figures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FigureCard = styled.figure`
  margin: 0;
  padding: 12px 0 26px;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  figcaption {
    margin: 0 0 12px;
    font-size: 0.9rem;
    line-height: 1.55;
    color: #222;
  }

  .ant-image {
    display: block;
    width: fit-content;
    max-width: 100%;
  }

  .ant-image-img {
    display: block;
    width: auto;
    max-width: 100%;
    height: auto;
  }

  .hint {
    display: block;
    margin-top: 6px;
    font-size: 0.7rem;
    color: #8c8c8c;
  }
`;

const Footnote = styled.footer`
  padding: 25px 0 0;
  font-size: 0.75rem;
  color: #8c8c8c;
`;

export default Publication;
